import { api } from "./client";

// AI помощникът в „Мониторинг" — само за служебния акаунт (userId === 1).
// Огледало на BE `app/features/assistant/schemas.py` (camelCase навън).

/**
 * Степента на сигурност — първият ред на отговора, разчетен в BE-то
 * (`prompt.split_confidence`). `null` — моделът е пропуснал етикета.
 */
export type AssistantConfidence = "confirmed" | "likely" | "unknown" | "clarify";

export interface AssistantStats {
  model: string;
  seconds: number;
  rounds: number;
  tokensIn: number;
  tokensOut: number;
  cacheRead: number;
  cacheWrite: number;
  /** Decimal от BE → JSON НИЗ (оценка в USD); `null` — моделът няма цена в BE. */
  costUsd: string | null;
  /** „read_section {…}", „user_access {…}" — в реда на извикване. */
  tools: string[];
}

export interface AssistantAnswer {
  /** Редът в `assistant_exchanges` — за оценката; `null`, ако записът не е минал. */
  id: number | null;
  /** Отговорът БЕЗ реда с етикета на сигурност. */
  answer: string;
  confidence: AssistantConfidence | null;
  /** Подава се със следващия въпрос; без него — нов разговор. */
  conversationId: string;
  stats: AssistantStats;
}

export type AssistantVote = "up" | "down";

export const askAssistant = async (
  question: string,
  conversationId: string | null,
): Promise<AssistantAnswer> => {
  const response = await api.post<AssistantAnswer>("/assistant/ask", {
    question,
    conversationId,
  });
  return response.data;
};

export const sendAssistantFeedback = async (
  id: number,
  vote: AssistantVote,
  comment: string,
): Promise<void> => {
  await api.post("/assistant/feedback", {
    id,
    vote,
    comment: comment.trim() || null,
  });
};

// Кодовете от `detail.code` на BE (`AssistantError` в
// app/features/assistant/service.py) → i18n ключ. Нов код там ⇒ ред тук +
// ключ в bg.ts И en.ts.
const ASSISTANT_ERROR_KEYS: Record<string, string> = {
  not_configured: "monitoring.assistantErrNotConfigured",
  conversation_expired: "monitoring.assistantErrExpired",
  timeout: "monitoring.assistantErrTimeout",
  upstream: "monitoring.assistantErrUpstream",
};

/** i18n ключът за провален въпрос; клиентският таймаут се брои за `timeout`. */
export const assistantErrorKey = (error: unknown): string => {
  const failure = error as {
    code?: string;
    response?: { status?: number; data?: { detail?: unknown } };
  };
  const detail = failure?.response?.data?.detail;
  const code =
    detail && typeof detail === "object"
      ? (detail as { code?: unknown }).code
      : undefined;
  if (typeof code === "string" && ASSISTANT_ERROR_KEYS[code]) {
    return ASSISTANT_ERROR_KEYS[code];
  }
  if (failure?.code === "ECONNABORTED" || failure?.code === "ETIMEDOUT") {
    return ASSISTANT_ERROR_KEYS.timeout;
  }
  if (failure?.response?.status === 403) return "monitoring.assistantErrForbidden";
  return "monitoring.assistantErrGeneric";
};

/** Дали разговорът трябва да започне отначало (изтекъл при OpenAI). */
export const isConversationExpired = (error: unknown): boolean =>
  assistantErrorKey(error) === ASSISTANT_ERROR_KEYS.conversation_expired;
